import { noop } from 'lodash';
import { createContext } from 'react';
import * as React from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { AISuggestionsMatcherArrayField } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';

import {
  EditTestamentaryEntityFormShape,
  EditTestamentaryEntitySection,
  EditTestamentaryEntitySectionOption,
} from '../EditTestamentaryEntitySplitScreen.types';

export type SubmitButtonType =
  | 'save'
  | 'prev'
  | 'next'
  | 'section-switcher'
  | null;

export interface EditTestamentaryEntitySplitScreenModalContext {
  // The testamentary entity being edited
  testamentaryEntityId: string;

  // Whether the household has two primary clients
  isTwoClientHousehold: boolean;

  // The current section being edited
  currentSection: EditTestamentaryEntitySection;
  setCurrentSection: (section: EditTestamentaryEntitySection) => void;

  // Whether the current section is the dispositions section
  isDPSection: boolean;

  // The currently selected first death client ID for the dispositions section.
  // This can be changed via the death order selector without closing the modal.
  selectedFirstDeathClientId: string | null;
  setSelectedFirstDeathClientId: (clientId: string | null) => void;

  // Track which button was clicked during form submission
  clickedSubmitButton: SubmitButtonType;
  setClickedSubmitButton: (button: SubmitButtonType) => void;

  // The available sections to edit
  sectionOptions: EditTestamentaryEntitySectionOption[];

  // The initial form values for all sections
  initialFormValues: EditTestamentaryEntityFormShape | undefined;

  // Indicates if there was an error fetching the entity data
  hasError: boolean;

  // Loading state for form data
  isLoading: boolean;

  // The ref to the footer of the modal, where the cancel/save buttons will be portaled to
  footerRef: React.RefObject<HTMLDivElement | null>;

  // The ref to the header of the modal, where the section switcher will be portaled to
  headerRef: React.RefObject<HTMLDivElement | null>;

  // Close the modal, with optional dirty state checking
  handleCloseModal: (skipChecks?: boolean) => void;

  // Refs to portal duplicates to after committing suggestion matches to the forms.
  duplicatesRefs: Partial<
    Record<
      AISuggestionsMatcherArrayField,
      React.RefObject<HTMLDivElement | null>
    >
  >;
}

export const getDefaultValues =
  (): EditTestamentaryEntitySplitScreenModalContext => ({
    testamentaryEntityId: '',
    isTwoClientHousehold: false,
    currentSection: EditTestamentaryEntitySection.BASIC_INFORMATION,
    setCurrentSection: noop,
    isDPSection: false,
    selectedFirstDeathClientId: null,
    setSelectedFirstDeathClientId: noop,
    clickedSubmitButton: null,
    setClickedSubmitButton: noop,
    sectionOptions: [],
    initialFormValues: undefined,
    hasError: false,
    isLoading: true,
    footerRef: React.createRef<HTMLDivElement>(),
    headerRef: React.createRef<HTMLDivElement>(),
    handleCloseModal: noop,
    duplicatesRefs: {},
  });

export const EditTestamentaryEntitySplitScreenModalContext =
  createContext<EditTestamentaryEntitySplitScreenModalContext>(
    getDefaultValues()
  );

export const useEditTestamentaryEntitySplitScreenModalContext = () => {
  return useGuardedContext(
    EditTestamentaryEntitySplitScreenModalContext,
    'EditTestamentaryEntitySplitScreenModalProvider'
  );
};
